import React, { useContext, useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Tabs, Loader } from 'foundations-library/components'
import API from 'api'
import { MyPhoneContactContext } from 'providers'
import CallSummary from './CallSummary'
import CallTranscript from './CallTranscription'

const useStyles = makeStyles((theme) => {
    const { colors } = theme['foundations-library']

    return {
        root: {
            marginTop: '20px',
            '& .tabs': {
                backgroundColor: colors.neutral[50],
                '& .tab': {
                    backgroundColor: 'transparent',
                    color: colors.neutral[700],
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    '&.active--tab': {
                        backgroundColor: `${colors.neutral[900]}!important`,
                        color: '#fff'
                    }
                }
            }
        }
    }
})

/**
 *
 */
export const tabs = {
    summary: {
        text: 'Call Summary & Action Items'
    },
    transcript: {
        text: 'Transcript'
    }
}

/**
 *
 */
const CallDetailsTabs = ({ UUID }: { UUID: string }): JSX.Element | null => {
    const classes = useStyles()
    const [activeTab, setActiveTab] = useState('summary')
    const [transcription, setTranscription] = useState<Record<string, any> | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const contactContext = useContext(MyPhoneContactContext)

    useEffect(() => {
        if (!UUID) return

        const numbersToContactMap = new Map<string, string>()

        const replaceNumbersWithName = (value: string) => {
            numbersToContactMap.forEach((displayName, number) => {
                const escapedNumber = number.replace(/[+.]/g, '\\$&')
                value = value.replace(new RegExp(escapedNumber, 'gi'), `<br/> **${displayName}** *(${number})*`)
            })
            return value
        }

        const filteredContent = (content: Record<string, any> | string): Record<string, any> | string => {
            if (typeof content === 'string') {
                return replaceNumbersWithName(content)
            } else if (typeof content === 'object' && content) {
                return Object.fromEntries(Object.entries(content).map(([key, value]: [string, any]) => {
                    return [key, filteredContent(value)]
                }))
            }
            return content
        }

        const findE164Numbers = (data: Record<string, any>): string[] => {
            const regex = /\+[\d]{10,15}/g
            const numbers: string[] = []

            const searchNumbers = (data: any) => {
                if (typeof data === 'string') {
                    const str = data
                    const matches = str.match(regex)
                    if (matches) {
                        numbers.push(...matches)
                    }
                } else if (typeof data === 'object' && data !== null) {
                    Object.values(data).forEach(searchNumbers)
                }
            }

            searchNumbers(data)
            return Array.from(new Set(numbers))
        }

        const getTranscription = async () => {
            setIsLoading(true)
            const responseData = await API.getCallTranscription(UUID) || {}
            if (!responseData.data) {
                setTranscription(null)
                setIsLoading(false)
                return
            }

            const transcriptionData = JSON.parse(responseData.data)
            const numbers = findE164Numbers(transcriptionData)

            for (const foundNumber of numbers) {
                const displayName = await contactContext.getDisplayName?.(foundNumber)
                if (displayName) {
                    numbersToContactMap.set(foundNumber, displayName)
                }
            }

            setTranscription(filteredContent(transcriptionData) as Record<string, any>)
            setIsLoading(false)
        }

        getTranscription()
    }, [UUID])

    if (isLoading) return <Loader />
    if (!transcription) return null

    return <div className={classes.root}>
        <Tabs
            value={activeTab}
            onChange={setActiveTab}
            items={Object.entries(tabs).map(([key, { text }]) => ({ value: key, text }))}
            className={'tabs'}
        />

        <div>
            {
                activeTab === 'summary' && <CallSummary summary={transcription?.summary} />
            }
            {
                activeTab === 'transcript' && <CallTranscript transcription={transcription?.transcription} />
            }
        </div>

    </div>
}

export default CallDetailsTabs
